import { ReactNode } from 'react';

import moment from 'moment';

import Tooltip from '@components/layout/Tooltip';

import { Badge, BadgeColor, BadgeVariant } from '@app/componentLibrary/Badge';
import { Card, CardContent } from '@app/componentLibrary/Card';
import Typography from '@app/componentLibrary/Typography';

import { MetadataItem } from './MetaDataItem';

interface BaseEntityMetadata {
    label: string;
}

interface ValueMetadata extends BaseEntityMetadata {
    value: string | ReactNode | null | undefined;
    date?: never;
    className?: string;
}

interface DateMetadata extends BaseEntityMetadata {
    value?: never;
    date: Date | string | null | undefined;
}

type EntityMetadata = ValueMetadata | DateMetadata;

interface MetadataGroup {
    metadata: EntityMetadata[];
}

interface EntityDetailsCardProps {
    title: string;
    titleBadge?: {
        label: string;
        color: BadgeColor;
        tooltip?: string;
        variant?: BadgeVariant;
    };
    secondaryTitleBadge?: {
        label: string;
        color: BadgeColor;
        tooltip?: string;
        variant?: BadgeVariant;
    };
    name: string;
    description?: string;
    status?: {
        label: string;
        color: BadgeColor;
        tooltip?: string;
        variant?: BadgeVariant;
    };
    customBadge?: ReactNode;
    image?: ReactNode;
    imageUrl?: string;
    imageAlt?: string;
    metadataGroups: MetadataGroup[];
    actions?: ReactNode;
    className?: string;
    testId?: string;
    appIsMobile: boolean;
}

export const EntityDetailsCard = (props: EntityDetailsCardProps) => {
    const {
        title,
        titleBadge,
        name,
        description,
        status,
        image,
        imageUrl,
        imageAlt,
        metadataGroups,
        actions,
        className,
        testId,
        secondaryTitleBadge,
        customBadge,
    } = props;

    const formatDate = (date: string | Date) => (date ? moment(date).format('MMM D, YYYY') : '--');

    return (
        <Card className={className} data-testid={testId} hasBorder={false}>
            <CardContent>
                <div className="flex md:items-center flex-col md:flex-row w-full">
                    {status && (
                        <div className="md:hidden flex md:items-center mb-4">
                            <Tooltip
                                title={status.tooltip}
                                disabled={!status.tooltip}
                                placement="right-end"
                            >
                                <span className="w-full">
                                    <Badge
                                        className="w-full justify-center"
                                        label={status.label}
                                        color={status.color}
                                        size="lg"
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    )}
                    {customBadge && (
                        <div className="md:hidden flex md:items-center w-full mb-4 [&_.spark-status-chip]:w-full [&_.spark-status-chip]:justify-center">
                            {customBadge}
                        </div>
                    )}
                    {/* Header Section */}
                    <div className="flex flex-row md:flex-col justify-start items-center flex-1 md:min-w-0 md:border-r md:pr-7 border-solid border-gray-100 max-md:border-b max-md:pb-4">
                        {/* Image + Status Section */}
                        <div className="flex md:w-full w-fit items-start md:items-center">
                            <div className="flex items-center">
                                {imageUrl && (
                                    <img
                                        src={imageUrl}
                                        alt={imageAlt || ''}
                                        className="w-16 h-16 rounded mr-3 object-contain"
                                    />
                                )}
                                {image && image}
                            </div>

                            {/* Title and Description - Desktop */}
                            <div className="md:flex flex-1 min-w-0 mr-10 hidden flex-col gap-1">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                    <Typography
                                        variant="sm"
                                        className="uppercase font-semibold text-gray-700 !mb-0 shrink-0"
                                    >
                                        {title}
                                    </Typography>
                                    {secondaryTitleBadge && (
                                        <span className="shrink-0">
                                            <Badge
                                                label={secondaryTitleBadge.label}
                                                color={secondaryTitleBadge.color}
                                                variant={secondaryTitleBadge.variant}
                                                size="sm"
                                            />
                                        </span>
                                    )}
                                    {titleBadge?.label && (
                                        <Tooltip
                                            title={titleBadge.tooltip || titleBadge.label}
                                            disabled={!titleBadge.tooltip && titleBadge.label.length <= 30}
                                            placement="right-end"
                                        >
                                            <span className="inline-flex min-w-0 max-w-[280px]">
                                                <Badge
                                                    className="max-w-full flex"
                                                    label={titleBadge.label}
                                                    color={titleBadge.color}
                                                    variant={titleBadge.variant}
                                                    size="sm"
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                                <Typography
                                    variant={name.length > 20 ? 'xl' : '2xl'}
                                    className="font-bold break-words"
                                >
                                    {name}
                                </Typography>
                                {description && (
                                    <Typography variant="base" className="text-gray-700">
                                        {description}
                                    </Typography>
                                )}
                            </div>

                            {/* Status Badge - desktop */}
                            {status && !customBadge && (
                                <div className="md:flex hidden md:items-center">
                                    <Tooltip
                                        title={status.tooltip}
                                        disabled={!status.tooltip}
                                        placement="right-end"
                                    >
                                        <span>
                                            <Badge
                                                className="text-nowrap"
                                                label={status.label}
                                                color={status.color}
                                                variant={status.variant}
                                                size="lg"
                                            />
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                            {customBadge && (
                                <div className="md:flex hidden md:items-center">{customBadge}</div>
                            )}
                        </div>

                        {/* Title and Description - Mobile */}
                        <div className="md:hidden flex w-full ml-1 min-w-0">
                            <div className="flex flex-col gap-1 min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                    <Typography
                                        variant="sm"
                                        className="uppercase font-semibold text-gray-700 !mb-0 shrink-0"
                                    >
                                        {title}
                                    </Typography>
                                    {secondaryTitleBadge && (
                                        <span className="shrink-0">
                                            <Badge
                                                label={secondaryTitleBadge.label}
                                                color={secondaryTitleBadge.color}
                                                size="sm"
                                            />
                                        </span>
                                    )}
                                    {titleBadge?.label && (
                                        <Tooltip
                                            title={titleBadge.tooltip || titleBadge.label}
                                            disabled={!titleBadge.tooltip && titleBadge.label.length <= 30}
                                            placement="right-end"
                                        >
                                            <span className="inline-flex min-w-0 max-w-[280px]">
                                                <Badge
                                                    className="max-w-full flex"
                                                    label={titleBadge.label}
                                                    color={titleBadge.color}
                                                    size="sm"
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                                <Typography variant="2xl" className="font-bold break-words">
                                    {name}
                                </Typography>
                                {description && (
                                    <Typography variant="base">{description}</Typography>
                                )}
                            </div>
                            {/* Mobile Actions */}
                            <div className="md:hidden ml-auto">{actions}</div>
                        </div>
                    </div>
                    <div className="md:flex md:items-center md:flex-wrap" data-chromatic="ignore">
                        {metadataGroups.map((group, groupIndex) => (
                            <div
                                key={groupIndex}
                                className={`
                                    grid grid-rows-2 grid-flow-col auto-cols-max gap-x-8 gap-y-4
                                    max-md:mt-4
                                    md:px-4
                                    flex-shrink-0
                                    ${
                                        groupIndex < metadataGroups.length - 1
                                            ? 'max-md:border-b max-md:pb-4 md:border-r border-solid border-gray-100'
                                            : ''
                                    }
                                `}
                            >
                                {group.metadata.map((item, index) => (
                                    <MetadataItem
                                        key={index}
                                        label={item.label}
                                        value={
                                            item.date ? formatDate(item.date) : item.value || '-'
                                        }
                                    />
                                ))}
                            </div>
                        ))}
                        {/* Desktop Actions */}
                        <div className="max-md:hidden md:flex md:items-center md:justify-center md:ml-auto">
                            {actions}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
